import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	App,
	Button,
	Card,
	Checkbox,
	Dropdown,
	Input,
	Modal,
	Select,
	Space,
	Typography,
} from 'antd';

import ConditionBuilder from '../common/ConditionBuilder';
import NotifyActionFields, {
	KNOWN_NOTIFY_KEYS,
	buildNotifyAction,
	emptyNotifyDraft,
	notifyDraftFromAction,
	useCollectionAudienceOptions,
} from './NotifyActionFields';
import TokenInput from '../common/TokenInput';
import TagSelect from '../tags/TagSelect';
import useCollectionTags from '../../hooks/useCollectionTags';
import { actionTagBlockedReason, automationTagRefusal, linkElementsTo } from '../../utils/tagRules';
import ToggleRow from '../common/ToggleRow';
import AlwaysRunToggle, { alwaysRunApplies, alwaysRunPayload, useAlwaysRun } from './AlwaysRunOnCardChange';
import { InputSelect } from '../Input';
import { buildCardHierarchyOptions } from '../cards/CardVisuals';
import { getDictionaryFromName } from '../../utils/dictionary';
import { buildEachTokens, buildEntryTokens, buildTriggerTokens } from '../../utils/automationTokens';
import { columnsOfGroup, entryConditionInside } from '../../utils/entryRules';
import { copySourceOf, copyTokenFor, describeCopy } from '../../utils/copyEntries';
import { LOOKUP_SOURCE_REFUSED, lookupSourceOptions, steeringToken } from '../../utils/lookupSources';
import LookupSourceField from './LookupSourceField';
import { createAutomation, patchAutomation } from '../../services/automations';
import { isServerAssignedElement } from '../../utils/elements';
import { isFileElement } from '../../utils/fileValues';
import { normalizeId } from '../../utils/ids';
import { userElementsOf } from '../../utils/userElements';
import { CLOSE, PLUS } from '../icons';
import useElementChoices from '../../hooks/useElementChoices';
import QueryRefusalPick from '../items/QueryRefusalPick';
import { choiceListOf } from '../../utils/choiceValues';
import { isMultiMeasure } from '../../utils/units';

/**
 * Create/edit one user-authored event rule (`kind: 'rule'`).
 *
 * Progressive sections, like DrivenFromModal and for the same reason: every
 * later choice depends on an earlier one (which elements exist depends on the
 * trigger card; which link-backs are legal depends on the target card), so a
 * section appears once its inputs are answerable — and nothing already answered
 * is hidden behind a step the user has to walk back through.
 *
 * The API validates a rule's ENTIRE shape at write time — card and element
 * references, KQL parse/compile, template tokens, action legality — and answers
 * with field-level messages written for humans. This editor therefore does not
 * try to re-implement that validation; it builds the documented JSON, omits
 * every empty optional key (the API is strict about shapes, not about defaults
 * it can derive), and surfaces a rejection verbatim. Guessing at the rules here
 * would only produce a second, drifting copy of them.
 */

const { Text } = Typography;

const EVENT_OPTIONS = [
	{ label: 'Created', value: 'created' },
	{ label: 'Updated', value: 'updated' },
	{ label: 'Deleted', value: 'deleted' },
	// Automatic tags (T6a, docs/plans/tags.md §3.4): a named tag arriving on
	// an item that had it neither by hand nor by a rule, or leaving both.
	// They need `trigger.tagIds`; private tagging never fires them.
	{ label: 'Tagged', value: 'tagged' },
	{ label: 'Untagged', value: 'untagged' },
];

const ALL_EVENTS = [ 'created', 'updated', 'deleted' ];
const TAG_EVENTS = [ 'tagged', 'untagged' ];
const listensForTags = events => ( events || [] ).some( event => TAG_EVENTS.includes( event ) );
const idList = value => ( Array.isArray( value ) ? value.map( id => normalizeId( id ) ).filter( Boolean ) : [] );

const EVALUATE_ON_OPTIONS = [
	{ value: 'transition', label: 'When it becomes true (default)' },
	{ value: 'match', label: 'Whenever it is true' },
];

// A rule that watches a repeating group's ENTRIES (keepr-api KPR-687,
// `trigger.type: 'entry-event'`): what can happen to an entry. A moved entry
// fires nothing, and a deleted record is not its entries' removal.
const ENTRY_EVENT_OPTIONS = [
	{ label: 'Added', value: 'added' },
	{ label: 'Changed', value: 'changed' },
	{ label: 'Removed', value: 'removed' },
];
const ALL_ENTRY_EVENTS = [ 'added', 'changed', 'removed' ];
// The item's condition on an entry rule is asked as the item STANDS (the
// edge is the entry's), so there "whenever it is true" is the default.
const ITEM_EVALUATE_ON_FOR_ENTRIES = [
	{ value: 'match', label: 'Whenever it is true (default)' },
	{ value: 'transition', label: 'When it becomes true' },
];

const isGroupElement = el => el?.dataType === 'repeating-group' && el?.name;
// A calculated column is the formula's (keepr-api KPR-686): no rule writes it.
const isCalculatedColumn = col => !! col?.options?.drivenFrom;

const ACTION_LABELS = {
	'set-entry': 'Set columns on the entry',
	'set-elements': 'Set elements',
	'update-items': 'Update related items',
	'adjust-elements': 'Add to or subtract from values',
	'create-item': 'Create an item',
	notify: 'Send a notification',
};

let keySeq = 0;
const nextKey = () => `k${ ++keySeq }`;

const elementLabelOf = el => el?.label?.singular || el?.name;

/**
 * Element rows a rule may WRITE. Driven elements are engine-owned
 * everywhere; SERVER-NUMBERED ones (`options.sequence`) are owned by the
 * allocator for a different reason and are just as unwritable — the API
 * refuses both at authoring, and offering a field the save will reject is
 * how an author learns the rule only after typing it.
 */
// A file element is never an action's to set either (PR 10b): an automation
// cannot attach a file, and the API refuses one (400 file_not_settable). A
// "Me too" count (KPR-390) is server assigned in the same sense — only the
// people who confirm move it (400 confirmations_not_settable; run outcome
// `counted`) — and isServerAssignedElement says so; nor is it ADJUSTABLE.
export const writableElements = elements => ( elements || [] )
	.filter( el => el?.name && ! isServerAssignedElement( el ) && ! isFileElement( el ) );

/**
 * A stored element (or lookup) name no card in scope offers: shown, flagged,
 * and kept. A rule written before this picker, by the API, or against a card
 * that has since changed can name something the cards no longer have; that
 * name is never shown blank and never dropped (a no-op save writes it back as
 * it was). The real choices stay in the list, so the fix is one pick.
 *
 * The flagged entry is disabled: it is what the rule holds, not a choice.
 * `options` may be grouped by card ([{ label, options }]).
 */
const NOT_FOUND = ' (not found)';
const optionValues = options => ( options || [] ).flatMap( option => ( option.options ? option.options.map( inner => inner.value ) : [ option.value ] ) );
const withStoredOption = ( options, stored ) => {
	const name = ( stored || '' ).trim();
	if ( ! name || optionValues( options ).includes( stored ) ) return { options, missing: false };
	return { options: [ { value: stored, label: `${ stored }${ NOT_FOUND }`, disabled: true }, ...options ], missing: true };
};

/**
 * Picker options over a POOL of cards (`[{ id, name, elements }]`): the
 * elements `pick` keeps from each card, by label. One card is a flat list;
 * several are grouped under the card's name. The same stored name on two
 * cards is offered under each card (so a person sees which cards it reaches)
 * and means one thing: the name. Each option has its own key for that reason.
 */
const poolOptions = ( pool, pick, labelOf = elementLabelOf ) => {
	const groups = pool
		.map( card => ( {
			label: card.name,
			options: ( card.elements || [] ).filter( pick ).map( el => ( { value: el.name, label: labelOf( el ), key: `${ card.id }:${ el.name }` } ) ),
		} ) )
		.filter( group => group.options.length );
	return groups.length > 1 ? groups : ( groups[ 0 ]?.options || [] );
};

/**
 * One name, several cards, different types (a number on one, text on another):
 * the rule writes the same value to each. Allowed - the API reads each card by
 * its own definition and skips what a card refuses - but said in a sentence.
 * Returns null when the name is one type everywhere in the pool.
 */
const typeCollision = ( pool, name, pick ) => {
	const found = ( pool || [] ).flatMap( card => ( card.elements || [] )
		.filter( el => el?.name === name && pick( el ) )
		.map( el => ( { card: card.name, type: getDictionaryFromName( el.dataType )?.label || el.dataType } ) ) );
	if ( new Set( found.map( entry => entry.type ) ).size < 2 ) return null;
	return `"${ name }" is ${ found.map( entry => `${ /^[aeiou]/i.test( entry.type ) ? 'an' : 'a' } ${ entry.type } on ${ entry.card }` ).join( ' and ' ) }. The same value is written to each, and a card that cannot take it skips it.`;
};

// What `adjust-elements` can move. Mirrors the API's own set: numbers and
// measurements, and deliberately NOT `rating` — a rating carries its own
// 0..max scale in a different validator, and it is an opinion rather than a
// quantity.
const ADJUSTABLE_TYPES = new Set( [ 'number', 'decimal', 'integer', 'measurement' ] );
// A measurement that allows several measures (KPR-1085) is not one: the API
// refuses adding an amount to it.
const isAdjustable = el => ADJUSTABLE_TYPES.has( el?.dataType ) && el?.options?.allowMultiple !== true
	&& ! ( el?.dataType === 'measurement' && isMultiMeasure( el?.options || {} ) );

/** The unit an amount is read in when it carries none: the element's own. */
const unitSuffixOf = el => ( el?.dataType === 'measurement' ? ( el?.options?.defaultUnit || '' ) : '' );

const rowsFromObject = obj => Object.entries( obj || {} ).map( ( [ name, value ] ) => ( {
	key: nextKey(),
	name,
	// Values round-trip as text: the inputs are text inputs, and the engine
	// re-types a value that is exactly one token anyway. A LIST stays a list:
	// the records a lookup's value names (KPR-1104) are stored as one, and
	// joined into one string they would be one string no lookup can hold.
	value: value === null || value === undefined ? '' : ( Array.isArray( value ) ? value : `${ value }` ),
} ) );

/** The card-lookups among `elements`, by name (the last definition of a name wins, as a merged list reads). */
const lookupsByName = elements => {
	const byName = new Map();
	( elements || [] ).forEach( el => { if ( el?.name ) byName.set( el.name, el ); } );
	return new Map( [ ...byName ].filter( ( [ , el ] ) => el?.dataType === 'card-lookup' ) );
};

/** The 400 that says a lookup is fed by something it cannot hold (KPR-1104), in words. */
const lookupSourceRefusal = err => ( err?.response?.data?.code === LOOKUP_SOURCE_REFUSED
	? 'A lookup can only be filled from a link or a chosen record. Pick a source or a record for the flagged value, then save again.'
	: null );

const rowsToObject = rows => {
	const out = {};
	( rows || [] ).forEach( row => {
		const name = ( row?.name || '' ).trim();
		if ( ! name ) return;
		out[ name ] = row?.value ?? '';
	} );
	return out;
};

const emptyRow = () => ( { key: nextKey(), name: '', value: '' } );

// The keys this editor OWNS per action type. Anything else on a stored
// action (future releases, API-authored rules) is carried through untouched
// on save — the API stores `actions` verbatim, so this editor is the one
// place a no-op save could otherwise erase data.
const KNOWN_ACTION_KEYS = {
	'set-entry': [ 'type', 'columns' ],
	'set-elements': [ 'type', 'elements' ],
	// `addTags` stays OWNED (so never carried through) though nothing here
	// reads or writes it: the API refuses it, and the maintenance run that
	// removed the tags before these took it off every stored rule. Should one
	// still carry it, a save drops it without a word (ruled b08: nothing of
	// those tags is shown). Tags ride `addTagIds` / `removeTagIds` (T6a):
	// collection tags, put on and taken off by hand for the rule.
	'update-items': [ 'type', 'scope', 'set', 'addTags', 'addTagIds', 'removeTagIds' ],
	// ⚠ An omission here would ERASE stored config on a no-op save: the API
	// stores `actions` verbatim and this editor carries through only the keys
	// it does not own.
	'adjust-elements': [ 'type', 'scope', 'by', 'onBound', 'whenEmpty' ],
	'create-item': [ 'type', 'card_id', 'elements', 'linkBackElement', 'forEach', 'dedupe' ],
	notify: KNOWN_NOTIFY_KEYS,
};
const KNOWN_SCOPE_KEYS = [ 'via', 'kql', 'linkElementName', 'card_id' ];
// The fan-out scope carries one key its sibling does not.
const KNOWN_FOR_EACH_KEYS = [ ...KNOWN_SCOPE_KEYS, 'max' ];

const omitKeys = ( obj, keys ) => Object.fromEntries(
	Object.entries( obj || {} ).filter( ( [ k ] ) => ! keys.includes( k ) )
);

const draftFromAction = action => {
	const key = nextKey();
	if ( action?.type === 'set-entry' ) {
		return { key, type: 'set-entry', raw: action, rows: rowsFromObject( action.columns ) };
	}
	if ( action?.type === 'set-elements' ) {
		return { key, type: 'set-elements', raw: action, rows: rowsFromObject( action.elements ) };
	}
	if ( action?.type === 'update-items' ) {
		const scope = action.scope || {};
		return {
			key,
			type: 'update-items',
			raw: action,
			via: scope.via === 'kql' ? 'kql' : 'lookup',
			cardId: normalizeId( scope.card_id ) || '',
			linkElementName: scope.linkElementName || '',
			kql: scope.kql || '',
			rows: rowsFromObject( action.set ),
			addTagIds: idList( action.addTagIds ),
			removeTagIds: idList( action.removeTagIds ),
		};
	}
	if ( action?.type === 'adjust-elements' ) {
		const scope = action.scope || null;
		return {
			key,
			type: 'adjust-elements',
			raw: action,
			// 'trigger' is the scope-less form — the commonest case, and the one
			// the API spells by OMITTING `scope` entirely.
			via: ! scope ? 'trigger' : ( scope.via === 'kql' ? 'kql' : 'lookup' ),
			cardId: normalizeId( scope?.card_id ) || '',
			linkElementName: scope?.linkElementName || '',
			kql: scope?.kql || '',
			rows: rowsFromObject( action.by ),
			onBound: action.onBound === 'skip' ? 'skip' : 'clamp',
			whenEmpty: action.whenEmpty === 'skip' ? 'skip' : 'zero',
		};
	}
	if ( action?.type === 'create-item' ) {
		const forEach = action.forEach || null;
		return {
			key,
			type: 'create-item',
			raw: action,
			cardId: normalizeId( action.card_id ) || '',
			rows: rowsFromObject( action.elements ),
			linkBackElement: action.linkBackElement || '',
			// 'none' is the single create this action has always been; the two
			// scope kinds are update-items' own vocabulary, shared.
			forEachVia: forEach ? ( forEach.via === 'kql' ? 'kql' : 'lookup' ) : 'none',
			forEachCardId: normalizeId( forEach?.card_id ) || '',
			forEachLinkElementName: forEach?.linkElementName || '',
			forEachKql: forEach?.kql || '',
			forEachMax: Number.isFinite( Number( forEach?.max ) ) ? `${ forEach.max }` : '',
			dedupeOn: Array.isArray( action.dedupe?.on ) ? action.dedupe.on : [],
		};
	}
	if ( action?.type === 'notify' ) {
		return { key, type: 'notify', raw: action, notify: notifyDraftFromAction( action ) };
	}
	return null;
};

const newDraft = type => {
	if ( type === 'update-items' ) {
		return { key: nextKey(), type, via: 'lookup', cardId: '', linkElementName: '', kql: '', rows: [ emptyRow() ], addTagIds: [], removeTagIds: [] };
	}
	if ( type === 'adjust-elements' ) {
		return {
			key: nextKey(), type, via: 'trigger', cardId: '', linkElementName: '', kql: '',
			rows: [ emptyRow() ], onBound: 'clamp', whenEmpty: 'zero',
		};
	}
	if ( type === 'create-item' ) {
		return {
			key: nextKey(), type, cardId: '', rows: [ emptyRow() ], linkBackElement: '',
			forEachVia: 'none', forEachCardId: '', forEachLinkElementName: '', forEachKql: '', forEachMax: '', dedupeOn: [],
		};
	}
	if ( type === 'notify' ) {
		return { key: nextKey(), type, notify: emptyNotifyDraft() };
	}
	if ( type === 'set-entry' ) {
		return { key: nextKey(), type, rows: [ emptyRow() ] };
	}
	return { key: nextKey(), type: 'set-elements', rows: [ emptyRow() ] };
};

const buildAction = draft => {
	const values = rowsToObject( draft.rows );
	// Unknown keys from the stored action ride along; the keys this editor
	// owns are rebuilt from the draft (and win the merge), and a key the
	// user emptied stays ABSENT — extras never resurrect it.
	const extras = omitKeys( draft.raw, KNOWN_ACTION_KEYS[ draft.type ] || [ 'type' ] );
	if ( draft.type === 'set-elements' ) {
		// Emitted even when empty: the API's "at least one element is
		// required" 400 must surface — silently dropping the action while
		// its panel is still on screen would be a lie.
		return { ...extras, type: 'set-elements', elements: values };
	}
	if ( draft.type === 'set-entry' ) {
		// Emitted even when empty, for the same reason ("at least one column").
		return { ...extras, type: 'set-entry', columns: values };
	}
	if ( draft.type === 'update-items' ) {
		const scopeExtras = omitKeys( draft.raw?.scope, KNOWN_SCOPE_KEYS );
		const scope = draft.via === 'kql'
			// The card confines a query scope too (the API enforces it), so it is
			// kept: dropping it here changed which items a stored rule touches.
			? { ...scopeExtras, via: 'kql', kql: ( draft.kql || '' ).trim(), ...( draft.cardId ? { card_id: draft.cardId } : {} ) }
			: {
				...scopeExtras,
				via: 'lookup',
				linkElementName: ( draft.linkElementName || '' ).trim(),
				...( draft.cardId ? { card_id: draft.cardId } : {} ),
			};
		const action = { ...extras, type: 'update-items', scope };
		if ( Object.keys( values ).length ) action.set = values;
		// Sent only when they name a tag: the API refuses an empty list, and an
		// omitted key is "no tags" on a replacing save.
		if ( ( draft.addTagIds || [] ).length ) action.addTagIds = draft.addTagIds;
		if ( ( draft.removeTagIds || [] ).length ) action.removeTagIds = draft.removeTagIds;
		return action;
	}
	if ( draft.type === 'adjust-elements' ) {
		const action = { ...extras, type: 'adjust-elements', by: values };
		if ( draft.via !== 'trigger' ) {
			const scopeExtras = omitKeys( draft.raw?.scope, KNOWN_SCOPE_KEYS );
			action.scope = draft.via === 'kql'
				? { ...scopeExtras, via: 'kql', kql: ( draft.kql || '' ).trim(), ...( draft.cardId ? { card_id: draft.cardId } : {} ) }
				: {
					...scopeExtras,
					via: 'lookup',
					linkElementName: ( draft.linkElementName || '' ).trim(),
					...( draft.cardId ? { card_id: draft.cardId } : {} ),
				};
		}
		// Both defaults are OMITTED rather than sent: the API derives them, and
		// a stored 'clamp' would be a second spelling of the same state for
		// every reader after this one.
		if ( draft.onBound === 'skip' ) action.onBound = 'skip';
		if ( draft.whenEmpty === 'skip' ) action.whenEmpty = 'skip';
		return action;
	}
	if ( draft.type === 'create-item' ) {
		const action = { ...extras, type: 'create-item', card_id: draft.cardId };
		if ( Object.keys( values ).length ) action.elements = values;
		if ( draft.linkBackElement ) action.linkBackElement = draft.linkBackElement;
		if ( draft.forEachVia && draft.forEachVia !== 'none' ) {
			const forEachExtras = omitKeys( draft.raw?.forEach, KNOWN_FOR_EACH_KEYS );
			const max = Number.parseInt( draft.forEachMax, 10 );
			action.forEach = {
				...forEachExtras,
				...( draft.forEachVia === 'kql'
					? { via: 'kql', kql: ( draft.forEachKql || '' ).trim() }
					: { via: 'lookup', linkElementName: ( draft.forEachLinkElementName || '' ).trim() } ),
				...( draft.forEachCardId ? { card_id: draft.forEachCardId } : {} ),
				...( Number.isFinite( max ) ? { max } : {} ),
			};
			const on = ( draft.dedupeOn || [] ).filter( Boolean );
			if ( on.length ) action.dedupe = { on };
		}
		return action;
	}
	if ( draft.type === 'notify' ) {
		// Event notifies get an "Open item" CTA at run time; any AUTHORED ctas
		// (API-written today — there is no builder for them on this side) ride
		// through verbatim rather than being erased by a no-op save.
		return buildNotifyAction( draft.notify, { extras, ctas: draft.raw?.ctas } );
	}
	return null;
};

export default function AutomationRuleEditor( {
	open,
	collectionId,
	rule,
	collectionCards = [],
	cardsById = {},
	getElementsForCard,
	onClose,
	onSaved,
} ) {
	const { message } = App.useApp();

	const [ name, setName ] = useState( '' );
	const [ triggerCardId, setTriggerCardId ] = useState( '' );
	const [ events, setEvents ] = useState( ALL_EVENTS );
	const [ elementsChanged, setElementsChanged ] = useState( [] );
	// Whether the rule also wakes on values KEEPR recalculates. Sent on every save:
	// this editor saves with `merge: false`, so a trigger key left out is deleted.
	const [ includeDerived, setIncludeDerived ] = useState( false );
	const [ alwaysRun, setAlwaysRun ] = useAlwaysRun( rule, open );
	// The tags a Tagged / Untagged rule listens for (`trigger.tagIds`).
	const [ triggerTagIds, setTriggerTagIds ] = useState( [] );
	const [ kql, setKql ] = useState( '' );
	const [ evaluateOn, setEvaluateOn ] = useState( 'transition' );
	// A rule that watches a repeating group's ENTRIES (KPR-687): the group
	// ('' = the item, an ordinary event rule), what happens to an entry, the
	// columns whose change counts, and the entry's own condition (the inside
	// of the block the API stores) with its "fire" choice.
	const [ entryGroup, setEntryGroup ] = useState( '' );
	const [ entryEvents, setEntryEvents ] = useState( [ 'added', 'changed' ] );
	const [ columnsChanged, setColumnsChanged ] = useState( [] );
	const [ entryKql, setEntryKql ] = useState( '' );
	const [ entryEvaluateOn, setEntryEvaluateOn ] = useState( 'transition' );
	const [ drafts, setDrafts ] = useState( [] );
	const [ saving, setSaving ] = useState( false );
	// A cross-item condition has no builder yet (it is authored through the
	// API). Dropping it on save would silently un-do somebody's rule, so it is
	// carried through verbatim and announced.
	const [ related, setRelated ] = useState( null );
	const [ droppedActions, setDroppedActions ] = useState( 0 );

	// Seeding happens on OPEN, never straight off the prop: Cancel has to be
	// able to discard edits.
	useEffect( () => {
		if ( ! open ) return;
		const trigger = rule?.trigger || {};
		setName( rule?.name || '' );
		setTriggerCardId( normalizeId( trigger.card_id ) || '' );
		// An entry rule's events are the ENTRY's: an item rule starts from all of them (KPR-687).
		setEvents( trigger.type !== 'entry-event' && Array.isArray( trigger.events ) && trigger.events.length ? trigger.events : ALL_EVENTS );
		setElementsChanged( Array.isArray( trigger.elementsChanged ) ? trigger.elementsChanged : [] );
		setIncludeDerived( trigger.includeDerived === true );
		setTriggerTagIds( idList( trigger.tagIds ) );
		setKql( trigger.condition?.kql || '' );
		const watchesEntries = trigger.type === 'entry-event';
		// On an entry rule the item's condition is asked as it stands unless it says otherwise.
		setEvaluateOn( trigger.condition?.evaluateOn || ( watchesEntries ? 'match' : 'transition' ) );
		setEntryGroup( watchesEntries ? ( trigger.group || '' ) : '' );
		setEntryEvents( watchesEntries && Array.isArray( trigger.events ) && trigger.events.length ? trigger.events : [ 'added', 'changed' ] );
		setColumnsChanged( watchesEntries && Array.isArray( trigger.columnsChanged ) ? trigger.columnsChanged : [] );
		setEntryKql( watchesEntries ? entryConditionInside( trigger.entryCondition?.kql ) : '' );
		setEntryEvaluateOn( watchesEntries && trigger.entryCondition?.evaluateOn === 'match' ? 'match' : 'transition' );
		setRelated( trigger.condition?.related?.length ? trigger.condition.related : null );
		const mapped = ( Array.isArray( rule?.actions ) ? rule.actions : [] ).map( draftFromAction );
		setDrafts( mapped.filter( Boolean ) );
		setDroppedActions( mapped.filter( draft => ! draft ).length );
	}, [ open, rule ] );

	const cardOptions = useMemo( () => buildCardHierarchyOptions( collectionCards ), [ collectionCards ] );

	// The collection's tags (and its parent's), asked for only once a rule
	// names tags — a Tagged / Untagged trigger, an action's Add tag / Remove
	// tag — or someone opens a tag picker. A rule that never touches tags
	// asks nothing.
	const [ tagsWanted, setTagsWanted ] = useState( false );
	const namesTags = tagsWanted || listensForTags( events ) || drafts.some( draft => ( draft.addTagIds || [] ).length || ( draft.removeTagIds || [] ).length );
	const { tags: collectionTags } = useCollectionTags( collectionId, { enabled: Boolean( open && collectionId && namesTags ) } );
	const wantTags = next => { if ( next ) setTagsWanted( true ); };

	// Groups and members only matter once a notify action exists — no reason to
	// spend a sharing request on a rule that only writes elements.
	const hasNotify = drafts.some( draft => draft.type === 'notify' );
	const { groups, users } = useCollectionAudienceOptions( collectionId, open && hasNotify );

	const elementsFor = useCallback(
		cardId => ( cardId && getElementsForCard ? ( getElementsForCard( cardId ) || [] ) : [] ),
		[ getElementsForCard ]
	);

	const triggerElements = useMemo( () => elementsFor( triggerCardId ), [ elementsFor, triggerCardId ] );
	// The trigger card's repeating groups (the last definition of a name wins,
	// as a merged list reads), and the one this rule watches, if any.
	const triggerGroups = useMemo( () => {
		const byName = new Map();
		triggerElements.forEach( el => { if ( el?.name ) byName.set( el.name, el ); } );
		return [ ...byName.values() ].filter( isGroupElement );
	}, [ triggerElements ] );
	const watchedGroup = useMemo( () => ( entryGroup ? triggerGroups.find( group => group.name === entryGroup ) || null : null ), [ entryGroup, triggerGroups ] );
	const watchedColumns = useMemo( () => columnsOfGroup( watchedGroup ), [ watchedGroup ] );
	// The item condition's hint, in the watched group's own words: its name
	// and its first yes/no column (else a placeholder the person replaces).
	const wholeGroupExample = useMemo( () => {
		const flag = watchedColumns.find( col => col.dataType === 'boolean' && ! isCalculatedColumn( col ) );
		return `not ${ watchedGroup?.name || 'group' } has (not ${ flag?.name || 'column' } = true)`;
	}, [ watchedGroup, watchedColumns ] );
	// A calculated column moves only when keepr recalculates it, so as a
	// column whose change sets the rule off it needs the recalculation switch.
	const calculatedWatched = useMemo( () => columnsChanged.filter( name => isCalculatedColumn( watchedColumns.find( col => col.name === name ) ) ), [ columnsChanged, watchedColumns ] );
	const watchesEntries = !! entryGroup;
	// The trigger card's user elements: the "People in element…" audience
	// (an event has a trigger item to read them off).
	const triggerUserElements = useMemo(
		() => userElementsOf( triggerElements ).map( el => ( { value: el.name, label: el.label?.singular || el.name } ) ),
		[ triggerElements ]
	);

	// The full event vocabulary: the trigger card's own elements, ONE dot-walk
	// hop through its card-lookups ({{trigger.staff.name}}), and the universals.
	const tokens = useMemo(
		() => [
			// {{entry.*}} on a rule that watches entries: the entry each action runs for.
			...( watchedGroup ? buildEntryTokens( watchedGroup ) : [] ),
			...buildTriggerTokens( triggerElements, getElementsForCard ),
		],
		[ watchedGroup, triggerElements, getElementsForCard ]
	);

	/**
	 * The vocabulary an update-items KQL scope may name. Its targets are
	 * OPEN-ENDED — the scope selects items of any card in the collection, not
	 * of the trigger card — so the field list is the union of every member
	 * card's merged elements, deduped by NAME because that is what KQL
	 * addresses. Two cards with a `status` element are one `status` field here;
	 * the API resolves it per matched item at run time, exactly as the raw text
	 * did before there was a builder.
	 *
	 * WHERE THE MERGE HAS TO GIVE UP. A name is one field here, but the typed
	 * value controls read the winning element's OPTIONS, and same-named elements
	 * do not have to agree about those. Two cards whose `owner` lookups point at
	 * DIFFERENT cards would offer the first card's items and store an id that
	 * means nothing to the second — silently wrong, and unrecoverable by the
	 * author. Two cards whose `status` choices differ is the same shape of
	 * problem pointing the other way: the picker would refuse to author a value
	 * the other card genuinely uses. So when same-named elements disagree, the
	 * ambiguous option is DROPPED from the merged element and the row falls back
	 * to a plain text input — which can express either card's value, and is what
	 * this box was before the typed controls existed.
	 */
	const allCollectionElements = useMemo( () => {
		const byName = new Map();
		const lookupTarget = element => normalizeId( element?.options?.lookupCardId ) || '';
		// A Choice's list only (KPE-033): a list another type carries is inert,
		// so it never makes two cards' elements read as different.
		const choiceValues = element => ( choiceListOf( element ).length
			? choiceListOf( element ).map( choice => `${ choice?.value ?? choice?.label ?? '' }` ).join( '\u0000' )
			: '' );
		const without = ( element, keys ) => ( {
			...element,
			options: Object.fromEntries(
				Object.entries( element.options || {} ).filter( ( [ key ] ) => ! keys.includes( key ) )
			),
		} );

		( collectionCards || [] ).forEach( card => {
			elementsFor( normalizeId( card?._id ) ).forEach( element => {
				const name = element?.name;
				if ( ! name ) return;
				const existing = byName.get( name );
				if ( ! existing ) { byName.set( name, element ); return; }
				const ambiguous = [];
				if ( lookupTarget( existing ) !== lookupTarget( element ) ) ambiguous.push( 'lookupCardId' );
				if ( choiceValues( existing ) !== choiceValues( element ) ) ambiguous.push( 'choices' );
				if ( ! ambiguous.length ) return;
				const merged = without( existing, ambiguous );
				// Two Choices whose lists differ: a Choice with no list would be an
				// empty picker, so the merged field reads as the short text both
				// cards' values are (KPE-033: only a Choice offers choices, and a
				// row on a short text is the plain text box the fallback means).
				byName.set( name, ambiguous.includes( 'choices' ) && merged.dataType === 'choice'
					? { ...merged, dataType: 'text-small', options: Object.fromEntries( Object.entries( merged.options ).filter( ( [ key ] ) => key !== 'allowMultiple' ) ) }
					: merged );
			} );
		} );
		return [ ...byName.values() ];
	}, [ collectionCards, elementsFor ] );

	// The trigger card and every ancestor of it: a card-lookup pointing at any
	// of them can serve as a create-item link-back (lookups include descendants,
	// and so does the API's own check).
	const triggerAncestorIds = useMemo( () => {
		const ids = new Set();
		let cursor = triggerCardId;
		while ( cursor && ! ids.has( cursor ) ) {
			ids.add( cursor );
			cursor = normalizeId( cardsById?.[ cursor ]?.parentCardId );
		}
		return ids;
	}, [ triggerCardId, cardsById ] );

	// The cards a linked-items step can reach. Named, it is that one card.
	// Not named, a lookup scope reaches EVERY card with a lookup that points at
	// the trigger card (or one above it) - what "Any card that links back" has
	// always meant - and a query scope every card of the collection. The
	// pickers offer the union of their elements, grouped by card.
	const poolOf = useCallback( cardId => ( {
		id: cardId,
		name: cardsById?.[ cardId ]?.name || '',
		elements: elementsFor( cardId ),
	} ), [ cardsById, elementsFor ] );
	const allCardPool = useMemo(
		() => ( collectionCards || [] ).map( card => poolOf( normalizeId( card?._id ) ) ).filter( card => card.id ),
		[ collectionCards, poolOf ]
	);
	const linkingCardPool = useMemo(
		() => allCardPool.filter( card => linkElementsTo( card.elements, triggerAncestorIds ).length ),
		[ allCardPool, triggerAncestorIds ]
	);
	const scopePool = ( cardId, via ) => ( cardId ? [ poolOf( cardId ) ] : ( via === 'lookup' ? linkingCardPool : allCardPool ) );
	const linkOptionsOf = pool => poolOptions(
		pool.map( card => ( { ...card, elements: linkElementsTo( card.elements, triggerAncestorIds ) } ) ),
		() => true
	);

	const patchDraft = useCallback( ( key, patch ) => {
		setDrafts( prev => prev.map( draft => ( draft.key === key ? { ...draft, ...patch } : draft ) ) );
	}, [] );

	const patchRow = useCallback( ( key, rowKey, patch ) => {
		setDrafts( prev => prev.map( draft => ( draft.key === key
			? { ...draft, rows: draft.rows.map( row => ( row.key === rowKey ? { ...row, ...patch } : row ) ) }
			: draft ) ) );
	}, [] );

	// An element a condition could mean several of (400 `ambiguous_element`,
	// element ids step 3): the person picks one, or any of these cards, and
	// the rule is saved again with the pick (`elementChoices`).
	// …answering exactly the texts it was made for (the conditions, the
	// actions' scopes and fan-outs, on their cards): any change forgets it.
	const elementPicks = useElementChoices( JSON.stringify( [ triggerCardId, kql, related, drafts, entryGroup, entryKql ] ) );
	const resetElementPicks = elementPicks.reset;
	// Each opening starts without picks.
	useEffect( () => { if ( open ) resetElementPicks(); }, [ open, resetElementPicks ] );

	const handleSave = async () => {
		// A rule that watches ENTRIES (KPR-687) builds its own trigger.
		if ( watchesEntries ) return saveRule( entryTrigger() );
		// Set columns on the entry is an entry rule's alone. One left over from
		// switching the trigger back to the item is said, never dropped on the
		// quiet (review of KPR-687): the person removes it, or watches an entry.
		if ( drafts.some( draft => draft.type === 'set-entry' ) ) {
			message.error( 'Set columns on the entry only works on a rule that watches entries. Choose an entry under What sets it off, or remove that action.' );
			return;
		}
		// An emptied Checkbox.Group means "all events", not "never fire" —
		// the API rejects an empty array, and no one unchecks every box to
		// disable a rule (that is what the switch is for).
		const trigger = { type: 'item-event', card_id: triggerCardId, events: events.length ? events : ALL_EVENTS };
		if ( elementsChanged.length ) trigger.elementsChanged = elementsChanged;
		// Sent only when ON, matching what the API stores — and sent at all
		// because of `merge: false` below. An omitted key under a replacing
		// PATCH is a DELETED key: without this line, saving a renamed rule
		// would silently drop somebody's opt-in and the rule would stop firing
		// on recalculations with a 200 and a success toast.
		if ( includeDerived ) trigger.includeDerived = true;
		// Tagged / Untagged listen for named tags, and need at least one.
		if ( listensForTags( trigger.events ) ) {
			if ( ! triggerTagIds.length ) {
				message.error( 'Pick the tags this rule listens for.' );
				return;
			}
			trigger.tagIds = triggerTagIds;
		}

		const condition = {};
		if ( kql.trim() ) condition.kql = kql.trim();
		if ( related?.length ) condition.related = related;
		// evaluateOn belongs to the CONDITION, not to the kql member — a
		// related-only condition stores it too (dropping it here silently
		// flipped such rules from level- to edge-triggered on a no-op save).
		// 'transition' is the API's own updated-event default and is omitted.
		if ( Object.keys( condition ).length && evaluateOn === 'match' ) condition.evaluateOn = 'match';
		// An emptied condition is sent as an explicit NULL, never left out.
		// This editor holds the whole rule, so "no condition" is something the
		// manager decided — and an omitted key is on its way to meaning "leave
		// the stored one alone", which would make clearing a condition here
		// silently do nothing.
		trigger.condition = Object.keys( condition ).length ? condition : null;
		return saveRule( trigger );
	};

	/**
	 * The `entry-event` trigger (KPR-687) as the API takes it. The entry
	 * condition is sent as the builder wrote it — the inside of a block; the
	 * API stores it as one block on the group. The item's condition is asked
	 * as it stands unless "when it becomes true" is chosen, so that is the
	 * value sent, and the default is left out.
	 */
	const entryTrigger = () => {
		const chosen = entryEvents.length ? entryEvents : ALL_ENTRY_EVENTS;
		const trigger = { type: 'entry-event', card_id: triggerCardId, group: entryGroup, events: chosen };
		// The changed-only choices are sent only while Changed is ticked, which
		// is when they are shown: none ticked means every event, but a choice
		// the person can no longer see must not narrow the rule.
		const changedTicked = entryEvents.includes( 'changed' );
		if ( changedTicked && columnsChanged.length ) trigger.columnsChanged = columnsChanged;
		if ( includeDerived ) trigger.includeDerived = true;
		if ( entryKql.trim() ) {
			trigger.entryCondition = { kql: entryKql.trim() };
			if ( changedTicked && entryEvaluateOn === 'match' ) trigger.entryCondition.evaluateOn = 'match';
		}
		const condition = {};
		if ( kql.trim() ) condition.kql = kql.trim();
		if ( related?.length ) condition.related = related;
		if ( Object.keys( condition ).length && evaluateOn === 'transition' ) condition.evaluateOn = 'transition';
		trigger.condition = Object.keys( condition ).length ? condition : null;
		return trigger;
	};

	const saveRule = async trigger => {
		const actions = drafts.map( buildAction ).filter( Boolean );
		const payload = { name: name.trim(), trigger, actions };
		Object.assign( payload, alwaysRunPayload( alwaysRunApplies( watchesEntries, events ), alwaysRun, rule ) );

		setSaving( true );
		try {
			// merge:false — this is a whole-rule editor and the payload above is
			// the complete state, so the stored trigger is REPLACED. Without it
			// a cleared `elementsChanged` would survive the save under a
			// merging PATCH (an empty list is not a shape the API accepts, so
			// there is no null to send for it). Ignored by an API that predates
			// the flag.
			if ( rule?._id ) await patchAutomation( collectionId, normalizeId( rule._id ), elementPicks.withChoices( { ...payload, merge: false } ) );
			else await createAutomation( collectionId, elementPicks.withChoices( payload ) );
			elementPicks.reset();
			message.success( rule?._id ? 'Rule saved.' : 'Rule created.' );
			onSaved?.();
		}
		catch ( err ) {
			// Verbatim: the API's 400s are field-level and written for humans —
			// except the tags' codes, whose sentences name the wire.
			if ( elementPicks.take( err ) ) return;
			message.error( automationTagRefusal( err ) || lookupSourceRefusal( err ) || err?.response?.data?.message || 'Could not save the rule.' );
		}
		finally {
			setSaving( false );
		}
	};

	const addActionMenu = {
		// "Set columns on the entry" exists only on a rule that watches entries.
		items: Object.entries( ACTION_LABELS )
			.filter( ( [ value ] ) => watchesEntries || value !== 'set-entry' )
			.map( ( [ value, label ] ) => ( { key: value, label } ) ),
		onClick: ( { key } ) => setDrafts( prev => [ ...prev, newDraft( key ) ] ),
	};

	/**
	 * The value rows of an action: an element picked from `elementOptions` (by
	 * its label; the stored name is the value) and its value.
	 *
	 * `blocked` is a sentence for when there is nothing to pick from yet: the
	 * card is not chosen, or it has no element that fits. It takes the place of
	 * the pickers - a name is never typed instead - and rows that already hold
	 * a name stay on screen, flagged, so a stored rule is never emptied.
	 */
	/**
	 * A create-item row whose element is a REPEATING GROUP of the new card
	 * (keepr-api KPR-1054): its value is never typed. It copies a group of the
	 * trigger item — picked from the trigger card's groups, stored as the whole
	 * `{{trigger.<group>}}` — and says which columns copy and which are left out.
	 */
	const renderGroupCopy = ( draft, row, target ) => {
		const options = triggerGroups.map( g => ( { value: copyTokenFor( g.name ), label: elementLabelOf( g ) } ) );
		const sourceName = copySourceOf( row.value );
		const source = sourceName ? triggerGroups.find( g => g.name === sourceName ) || null : null;
		const stored = ( row.value || '' ).trim();
		// What the rule holds and no group answers: kept, flagged, never shown as text.
		const shown = stored && ! source ? [ { value: row.value, label: 'A value that copies nothing (not found)', disabled: true }, ...options ] : options;
		const said = source ? describeCopy( source, target ) : null;
		const targetLabel = elementLabelOf( target );
		return (
			<div style={{ flex: '1 1 auto', minWidth: 200 }}>
				{options.length ? (
					<Select
						style={{ width: '100%' }}
						value={stored ? row.value : undefined}
						placeholder='Copy the entries of…'
						aria-label={`Copy entries into ${ targetLabel } from`}
						status={stored && ! source ? 'warning' : undefined}
						options={shown}
						onChange={value => patchRow( draft.key, row.key, { value } )}
					/>
				) : (
					<Text type='secondary' className='d-block'>The trigger card has no repeating group to copy entries from.</Text>
				)}
				{said ? (<>
					<Text type={said.refused ? 'warning' : 'secondary'} className='d-block'>{said.copies}</Text>
					{said.leftOut.length ? <Text type='secondary' className='d-block'>{`Left out: ${ said.leftOut.join( '; ' ) }.`}</Text> : null}
				</>) : null}
				{stored && ! source && options.length ? (
					<Text type='warning' className='d-block'>{`${ targetLabel } can only copy a group of the trigger item. Pick one from the list.`}</Text>
				) : null}
			</div>
		);
	};

	/**
	 * A row whose element is a CARD-LOOKUP where the action writes (KPR-1104):
	 * its value is never typed. It copies a link the rule reads, or names the
	 * records the manager chooses (LookupSourceField) — the only two things the
	 * API lets a rule fill a lookup with. `each` is the fan-out's card, when the
	 * action has one.
	 */
	const renderLookupSource = ( draft, row, target, { each = null } = {} ) => {
		const entryColumns = watchesEntries ? watchedColumns : null;
		const base = { triggerElements, entryColumns, getElementsForCard };
		// A fan-out whose query reads a typed value lets that person pick its
		// rows, so they are no source (the API: reason `steered`).
		const eachSteeredBy = each && each.kql ? steeringToken( each.kql, base ) : null;
		const ctx = { ...base, eachElements: each ? each.elements : null, eachSteeredBy };
		const sources = lookupSourceOptions( {
			target,
			triggerElements,
			triggerCardId,
			triggerCardName: cardsById?.[ triggerCardId ]?.name || '',
			eachElements: each ? each.elements : null,
			eachCardId: each ? each.cardId : '',
			eachCardName: each ? each.cardName : '',
			eachSteered: !! eachSteeredBy,
			entryColumns,
			entryLabel: watchedGroup ? ( elementLabelOf( watchedGroup ) || '' ).toLowerCase() : '',
			getElementsForCard,
			cardsById,
		} );
		return (
			<div style={{ flex: '1 1 auto', minWidth: 200 }}>
				<LookupSourceField
					value={row.value}
					onChange={value => patchRow( draft.key, row.key, { value } )}
					target={target}
					sources={sources}
					ctx={ctx}
					collectionId={collectionId}
					ariaLabel={`Value for ${ elementLabelOf( target ) || row.name }`}
					targetCardName={cardsById?.[ normalizeId( target?.options?.lookupCardId ) ]?.name || ''}
					note={eachSteeredBy ? 'Each matching item is not offered: a typed value picks which items match.' : ''}
				/>
			</div>
		);
	};

	const renderRows = ( draft, { elementOptions = [], extraTokens, nameLabel = 'Element', blocked = null, scopeName = '', collision = null, groupTargets = null, lookupTargets = null, each = null } ) => {
		const rows = blocked ? draft.rows.filter( row => row.name || row.value ) : draft.rows;
		return (<>
			{blocked ? <Text type='secondary' className='d-block' style={{ marginBottom: 8 }}>{blocked}</Text> : null}
			{rows.map( row => {
				const { options, missing } = withStoredOption( elementOptions, row.name );
				const collides = ! missing && collision ? collision( row.name ) : null;
				const copyTarget = groupTargets && row.name ? groupTargets.get( row.name ) || null : null;
				const lookupTarget = ! copyTarget && lookupTargets && row.name ? lookupTargets.get( row.name ) || null : null;
				return (
					<Fragment key={row.key}>
						<Space align='start' style={{ display: 'flex', marginBottom: 8 }}>
							<Select
								style={{ width: 200 }}
								value={row.name || undefined}
								placeholder={nameLabel}
								aria-label={nameLabel}
								status={missing || collides ? 'warning' : undefined}
								showSearch
								optionFilterProp='label'
								options={options}
								onChange={value => patchRow( draft.key, row.key, { name: value } )}
							/>
							{copyTarget ? renderGroupCopy( draft, row, copyTarget ) : lookupTarget ? renderLookupSource( draft, row, lookupTarget, { each } ) : (
								<div style={{ flex: '1 1 auto', minWidth: 200 }}>
									<TokenInput
										value={Array.isArray( row.value ) ? row.value.join( ', ' ) : row.value}
										tokens={extraTokens ? [ ...extraTokens, ...tokens ] : tokens}
										placeholder='Value (templates allowed)'
										ariaLabel={`Value for ${ row.name || nameLabel.toLowerCase() }`}
										onChange={value => patchRow( draft.key, row.key, { value } )}
									/>
								</div>
							)}
							<Button
								type='text'
								danger
								icon={<CLOSE />}
								aria-label='Remove this value'
								onClick={() => patchDraft( draft.key, { rows: draft.rows.filter( r => r.key !== row.key ) } )}
							/>
						</Space>
						{missing && ! blocked ? (
							<Text type='warning' className='d-block' style={{ marginTop: -4, marginBottom: 8 }}>
								{`"${ row.name }" is not an element that a rule can set on ${ scopeName || 'this card' }. It is kept as it is until you pick another.`}
							</Text>
						) : null}
						{collides ? (
							<Text type='warning' className='d-block' style={{ marginTop: -4, marginBottom: 8 }}>{collides}</Text>
						) : null}
					</Fragment>
				);
			} )}
			{blocked ? null : (
				<Button
					size='small'
					icon={<PLUS />}
					onClick={() => patchDraft( draft.key, { rows: [ ...draft.rows, emptyRow() ] } )}
				>
					Add value
				</Button>
			)}
		</>);
	};

	/**
	 * The lookup a linked-items step follows: picked from the lookups that point
	 * at the trigger card (or a card above it). On a named card, that card's;
	 * with no card named, those of every card that links back, grouped by card
	 * (a lookup of the same name on two cards is one choice that reaches both).
	 * It never takes a typed name. A stored lookup nothing offers stays, flagged.
	 */
	const renderLinkPick = ( { cardName, options, value, onChange } ) => {
		const { options: shown, missing } = withStoredOption( options, value );
		if ( ! options.length && ! value ) {
			return (
				<Text type='secondary' className='d-block' style={{ marginTop: 8 }}>
					{cardName ? `${ cardName } has no lookup that points at the trigger card.` : 'No card in this collection links back to the trigger card.'}
				</Text>
			);
		}
		return (<>
			<Select
				style={{ width: '100%', marginTop: 8 }}
				value={value || undefined}
				aria-label='Link element'
				placeholder='Which lookup points at the trigger item?'
				status={missing ? 'warning' : undefined}
				showSearch
				optionFilterProp='label'
				options={shown}
				onChange={onChange}
			/>
			{! options.length && cardName ? (
				<Text type='secondary' className='d-block'>{`${ cardName } has no lookup that points at the trigger card.`}</Text>
			) : null}
			{missing ? (
				<Text type='warning' className='d-block'>
					{`"${ value }" is not a lookup${ cardName ? ` on ${ cardName }` : '' } that points at the trigger item. Pick one from the list.`}
				</Text>
			) : null}
		</>);
	};

	const renderDraft = draft => {
		if ( draft.type === 'set-entry' ) {
			// The watched group's columns a rule may write: never a calculated one,
			// and never a file column (KPR-934; a rule cannot attach a file - the
			// API refuses `set-entry` on one at authoring, as `set-elements` does).
			const options = watchedColumns
				.filter( col => ! isCalculatedColumn( col ) && ! isFileElement( col ) )
				.map( col => ( { value: col.name, label: elementLabelOf( col ) } ) );
			return (<>
				<Text type='secondary'>
					{watchesEntries
						? `Writes these values onto each ${ ( elementLabelOf( watchedGroup ) || 'entry' ).toLowerCase() } entry that set the rule off.`
						: 'Only a rule that watches entries can set an entry\'s columns. Choose an entry above, or remove this action.'}
				</Text>
				<div style={{ marginTop: 8 }}>{renderRows( draft, { elementOptions: options, nameLabel: 'Column', lookupTargets: lookupsByName( watchedColumns ) } )}</div>
			</>);
		}
		if ( draft.type === 'set-elements' ) {
			const options = writableElements( triggerElements )
				.map( el => ( { value: el.name, label: elementLabelOf( el ) } ) );
			return (<>
				<Text type='secondary'>Writes these values back onto the item that triggered the rule.</Text>
				<div style={{ marginTop: 8 }}>{renderRows( draft, { elementOptions: options, scopeName: cardsById?.[ triggerCardId ]?.name || '', lookupTargets: lookupsByName( writableElements( triggerElements ) ) } )}</div>
			</>);
		}

		if ( draft.type === 'update-items' ) {
			const scopeCardName = cardsById?.[ draft.cardId ]?.name || '';
			// What the step reaches: the named card, else every card that links
			// back (or, for a query, every card). Elements are offered as the union
			// over those cards, grouped by card when there are several.
			const pool = scopePool( draft.cardId, draft.via );
			const scopeName = scopeCardName || ( draft.via === 'lookup' ? 'any card that links back' : 'any card of this collection' );
			const setPick = el => writableElements( [ el ] ).length > 0;
			const setOptions = poolOptions( pool, setPick );
			const linkOptions = linkOptionsOf( pool );
			return (<>
				<Select
					style={{ width: '100%', marginBottom: 8 }}
					value={draft.via}
					aria-label='How to choose the items to update'
					options={[
						{ value: 'lookup', label: 'Items that link back to the trigger item' },
						{ value: 'kql', label: 'Items matching a query' },
					]}
					onChange={value => patchDraft( draft.key, { via: value } )}
				/>

				<InputSelect
					elementName={`updateItemsCard-${ draft.key }`}
					elementLabel='Card to update'
					value={draft.cardId || undefined}
					onChange={( n, value ) => patchDraft( draft.key, { cardId: value || '', linkElementName: '' } )}
					options={cardOptions}
					placeholder={draft.via === 'lookup' ? 'Any card that links back' : 'Any card the query finds'}
				/>

				{draft.via === 'lookup' ? renderLinkPick( {
					cardName: scopeCardName,
					options: linkOptions,
					value: draft.linkElementName,
					onChange: value => patchDraft( draft.key, { linkElementName: value } ),
				} ) : (<div style={{ marginTop: 8 }}>
					<ConditionBuilder
						value={draft.kql}
						onChange={value => patchDraft( draft.key, { kql: value } )}
						elements={allCollectionElements}
						collectionId={collectionId}
					/>
					<Text type='secondary'>
						Selects the items to change, checked when you save.
					</Text>
				</div>)}

				<div style={{ marginTop: 12 }}>
					{setOptions.length ? (
						<Text type='secondary' className='d-block' style={{ marginBottom: 8 }}>
							Use the {'{{ }}'} menu in a value to insert something from the item that set the rule off.
						</Text>
					) : null}
					{renderRows( draft, {
						elementOptions: setOptions,
						scopeName,
						// A name that is a lookup on ANY card the step reaches takes only a
						// link or a chosen record: the API judges each target's own card.
						lookupTargets: new Map( pool.flatMap( card => [ ...lookupsByName( writableElements( card.elements ) ) ] ).reverse() ),
						collision: name => typeCollision( pool, name, setPick ),
						blocked: pool.length ? null : 'No card in this collection links back to the trigger card, so there is nothing to change yet.',
					} )}
				</div>

				<div className='automation-rule__tags' style={{ marginTop: 12 }}>
					<span className='d-block'>Add tag</span>
					<TagSelect
						multiple
						value={draft.addTagIds || []}
						collectionTags={collectionTags}
						privateTags={null}
						itemTags={false}
						placeholder='Tags to put on the items (optional)'
						aria-label='Add tag'
						onOpenChange={wantTags}
						disabledFor={tag => actionTagBlockedReason( tag ) || ( ( draft.removeTagIds || [] ).includes( normalizeId( tag?._id ) ) ? 'It is already being taken off.' : null )}
						onChange={ids => patchDraft( draft.key, { addTagIds: ids } )}
					/>
					<span className='d-block' style={{ marginTop: 8 }}>Remove tag</span>
					<TagSelect
						multiple
						value={draft.removeTagIds || []}
						collectionTags={collectionTags}
						privateTags={null}
						itemTags={false}
						placeholder='Tags to take off the items (optional)'
						aria-label='Remove tag'
						onOpenChange={wantTags}
						disabledFor={tag => actionTagBlockedReason( tag ) || ( ( draft.addTagIds || [] ).includes( normalizeId( tag?._id ) ) ? 'It is already being put on.' : null )}
						onChange={ids => patchDraft( draft.key, { removeTagIds: ids } )}
					/>
					<Text type='secondary' className='d-block'>
						The collection&apos;s tags, put on or taken off as if by hand. A restricted tag, or one only its own rule applies, can&apos;t be added or removed by a rule.
					</Text>
				</div>
			</>);
		}

		if ( draft.type === 'adjust-elements' ) {
			// The elements an ADJUSTMENT may name: numbers and measurements. A
			// text element has no arithmetic and a `rating` is an opinion
			// rather than a quantity (its scale lives in a different
			// validator) — the API refuses both, and offering a field the save
			// will reject is how an author learns the rule only after typing
			// it. A record found by its link or by a query is of the named card, or
			// of any card that links back (any card, for a query): the union of
			// those cards' numbers is offered, grouped by card.
			const pool = draft.via === 'trigger' ? [ poolOf( triggerCardId ) ] : scopePool( draft.cardId, draft.via );
			const scopeCardName = cardsById?.[ draft.via === 'trigger' ? triggerCardId : draft.cardId ]?.name || '';
			const scopeName = scopeCardName || ( draft.via === 'lookup' ? 'any card that links back' : 'any card of this collection' );
			const lookupOptions = linkOptionsOf( pool );
			const adjustPick = el => writableElements( [ el ] ).length > 0 && isAdjustable( el );
			// The unit is on the LABEL because the amount is read in it: "in kg"
			// invisible while typing is how somebody adds a thousand.
			const options = poolOptions( pool, adjustPick, el => ( unitSuffixOf( el ) ? `${ elementLabelOf( el ) } (in ${ unitSuffixOf( el ) })` : elementLabelOf( el ) ) );
			const adjustBlocked = ! pool.length
				? 'No card in this collection links back to the trigger card, so there is nothing to adjust yet.'
				: ( optionValues( options ).length ? null : ( scopeCardName ? `${ scopeCardName } has no numbers or measurements to add to.` : 'No card in this scope has numbers or measurements to add to.' ) );
			const cardPick = (
				<InputSelect
					elementName={`adjustCard-${ draft.key }`}
					elementLabel='Card to adjust'
					value={draft.cardId || undefined}
					onChange={( n, value ) => patchDraft( draft.key, { cardId: value || '', linkElementName: '' } )}
					options={cardOptions}
					placeholder={draft.via === 'lookup' ? 'Any card that links back' : 'Any card the query finds'}
				/>
			);
			// ⚠ THE ONE WAY A PERSON DESTROYS THEIR OWN DATA WITH THIS FEATURE.
			// An adjustment is not idempotent, so "whenever it is true" compounds
			// the amount on every save while the condition holds. The default
			// ('transition') saves most authors; this warns the ones who changed it.
			const compounds = watchesEntries
				? entryEvaluateOn === 'match' && entryEvents.includes( 'changed' ) && !! entryKql.trim()
				: evaluateOn === 'match' && events.includes( 'updated' );
			return (<>
				{compounds ? (
					<Alert
						type='warning'
						showIcon
						style={{ marginBottom: 8 }}
						message='This will add the amount again on every save'
						description={'Because this rule fires "whenever it is true" on an update, '
							+ 'the amount is applied each time the record is saved while the condition holds. '
							+ 'Switch Fire back to "When it becomes true" unless you mean it to compound.'}
					/>
				) : null}
				<Select
					style={{ width: '100%', marginBottom: 8 }}
					value={draft.via}
					aria-label='Which records to adjust'
					options={[
						{ value: 'trigger', label: 'The record that set the rule off' },
						{ value: 'lookup', label: 'Records that link back to it' },
						{ value: 'kql', label: 'Records matching a query' },
					]}
					onChange={value => patchDraft( draft.key, { via: value } )}
				/>

				{draft.via === 'lookup' ? (<>
					{cardPick}
					{renderLinkPick( {
						cardName: scopeCardName,
						options: lookupOptions,
						value: draft.linkElementName,
						onChange: value => patchDraft( draft.key, { linkElementName: value } ),
					} )}
				</>) : null}

				{draft.via === 'kql' ? (<>
					{cardPick}
					<div style={{ marginTop: 8 }}>
						<ConditionBuilder
							value={draft.kql}
							onChange={value => patchDraft( draft.key, { kql: value } )}
							elements={allCollectionElements}
							collectionId={collectionId}
						/>
						<Text type='secondary'>
							Selects the records to change, checked when you save.
						</Text>
					</div>
				</>) : null}

				<div style={{ marginTop: 12 }}>
					<Text type='secondary' className='d-block'>
						How much to add. A negative amount subtracts; a measurement takes a
						unit (&quot;30 min&quot;), and a bare number is read in the element&apos;s own unit.
						Use the {'{{ }}'} menu to insert a number from the item that set the rule off.
					</Text>
					{renderRows( draft, { elementOptions: options, scopeName, collision: name => typeCollision( pool, name, adjustPick ), blocked: adjustBlocked } )}
				</div>

				<Space wrap style={{ marginTop: 12 }}>
					<div>
						<label htmlFor={`onBound-${ draft.key }`} className='d-block'>If it would go out of range</label>
						<Select
							id={`onBound-${ draft.key }`}
							style={{ width: 280 }}
							value={draft.onBound}
							options={[
								{ value: 'clamp', label: 'Stop at the limit (recorded on the run)' },
								{ value: 'skip', label: 'Leave the value as it was' },
							]}
							onChange={value => patchDraft( draft.key, { onBound: value } )}
						/>
					</div>
					<div>
						<label htmlFor={`whenEmpty-${ draft.key }`} className='d-block'>If the value is blank</label>
						<Select
							id={`whenEmpty-${ draft.key }`}
							style={{ width: 240 }}
							value={draft.whenEmpty}
							options={[
								{ value: 'zero', label: 'Start from zero' },
								{ value: 'skip', label: 'Leave it blank' },
							]}
							onChange={value => patchDraft( draft.key, { whenEmpty: value } )}
						/>
					</div>
				</Space>
			</>);
		}

		if ( draft.type === 'notify' ) {
			// The FULL token vocabulary here: an event rule has a trigger item,
			// so {{trigger.*}} renders (and the API accepts it).
			return (
				<NotifyActionFields
					draft={draft.notify}
					onChange={notify => patchDraft( draft.key, { notify } )}
					tokens={tokens}
					groups={groups}
					users={users}
					userElements={triggerUserElements}
					collectionId={collectionId}
					// Built at click time from THIS panel's current draft — a rule
					// may carry several notify actions, and each previews itself.
					buildPreviewRequest={() => ( {
						kind: 'rule',
						// A rule that watches entries previews one entry ({{entry.*}}).
						trigger: watchesEntries ? { type: 'entry-event', card_id: triggerCardId, group: entryGroup } : { card_id: triggerCardId },
						action: buildAction( draft ),
					} )}
				/>
			);
		}

		// create-item
		const targetElements = elementsFor( draft.cardId );
		const options = writableElements( targetElements )
			.map( el => ( { value: el.name, label: elementLabelOf( el ) } ) );
		// The new card's repeating groups: each fills by copying a group of the
		// trigger item (KPR-1054), the last definition of a name winning.
		const groupTargets = new Map();
		targetElements.forEach( el => { if ( el?.name ) groupTargets.delete( el.name ); if ( isGroupElement( el ) ) groupTargets.set( el.name, el ); } );
		const targetCardName = cardsById?.[ draft.cardId ]?.name || '';
		const linkBackOptions = linkElementsTo( targetElements, triggerAncestorIds )
			.map( el => ( { value: el.name, label: elementLabelOf( el ) } ) );
		const fanOut = draft.forEachVia && draft.forEachVia !== 'none';
		const forEachElements = elementsFor( draft.forEachCardId );
		const forEachCardName = cardsById?.[ draft.forEachCardId ]?.name || '';
		// Not named, the fan-out follows every card that links back.
		const forEachLinkOptions = linkOptionsOf( scopePool( draft.forEachCardId, 'lookup' ) );
		// The names the dedupe key may use: what this action actually writes.
		// A key on anything else would match every blank row and suppress
		// every create — the API refuses it, and the picker never offers it.
		const writtenNames = ( draft.rows || [] )
			.map( row => ( row?.name || '' ).trim() )
			.filter( Boolean )
			.concat( draft.linkBackElement ? [ draft.linkBackElement ] : [] );
		const dedupeOptions = Array.from( new Set( writtenNames ) ).map( name => ( {
			value: name,
			label: elementLabelOf( targetElements.find( el => el?.name === name ) ) || name,
		} ) );
		return (<>
			<InputSelect
				elementName={`createItemCard-${ draft.key }`}
				elementLabel='Card to create'
				value={draft.cardId || undefined}
				onChange={( n, value ) => patchDraft( draft.key, { cardId: value || '', linkBackElement: '' } )}
				options={cardOptions}
				placeholder='Which card is the new item?'
				allowClear={false}
			/>
			<div style={{ marginTop: 12 }}>
				<label htmlFor={`forEachVia-${ draft.key }`} className='d-block'>How many items</label>
				<Select
					id={`forEachVia-${ draft.key }`}
					style={{ width: '100%' }}
					value={draft.forEachVia || 'none'}
					options={[
						{ value: 'none', label: 'One item, each time the rule fires' },
						{ value: 'lookup', label: 'One item for each item that links back to the trigger' },
						{ value: 'kql', label: 'One item for each item matching a query' },
					]}
					onChange={value => patchDraft( draft.key, {
						forEachVia: value,
						...( value === 'none' ? { dedupeOn: [] } : {} ),
					} )}
				/>
			</div>

			{fanOut ? (
				<Card size='small' style={{ marginTop: 12 }} title='For each matching item'>
					<InputSelect
						elementName={`forEachCard-${ draft.key }`}
						elementLabel={draft.forEachVia === 'kql' ? 'Card the matches come from' : 'Card to match'}
						value={draft.forEachCardId || undefined}
						onChange={( n, value ) => patchDraft( draft.key, { forEachCardId: value || '', forEachLinkElementName: '' } )}
						options={cardOptions}
						placeholder={draft.forEachVia === 'kql' ? 'Any card (naming one types the tokens)' : 'Any card that links back'}
					/>

					{draft.forEachVia === 'lookup' ? renderLinkPick( {
						cardName: forEachCardName,
						options: forEachLinkOptions,
						value: draft.forEachLinkElementName,
						onChange: value => patchDraft( draft.key, { forEachLinkElementName: value } ),
					} ) : (<div style={{ marginTop: 8 }}>
						<ConditionBuilder
							value={draft.forEachKql}
							onChange={value => patchDraft( draft.key, { forEachKql: value } )}
							elements={allCollectionElements}
							collectionId={collectionId}
						/>
						<Text type='secondary'>
							Selects what to create one item for, checked when you save.
						</Text>
					</div>)}

					<div style={{ marginTop: 8 }}>
						<label htmlFor={`forEachMax-${ draft.key }`} className='d-block'>At most</label>
						<Input
							id={`forEachMax-${ draft.key }`}
							style={{ maxWidth: 220 }}
							value={draft.forEachMax}
							inputMode='numeric'
							placeholder='200'
							suffix='items per run'
							onChange={e => patchDraft( draft.key, { forEachMax: e.target.value.replace( /[^0-9]/g, '' ) } )}
						/>
					</div>

					<div style={{ marginTop: 12 }}>
						<label htmlFor={`dedupe-${ draft.key }`} className='d-block'>Don&apos;t create a duplicate when these match</label>
						<Select
							id={`dedupe-${ draft.key }`}
							mode='multiple'
							style={{ width: '100%' }}
							value={draft.dedupeOn}
							placeholder='Pick the values that identify one record (e.g. the order and the date)'
							options={dedupeOptions}
							onChange={value => patchDraft( draft.key, { dedupeOn: value } )}
						/>
						<Text type='secondary'>
							Without this, running the rule twice writes the work twice.
						</Text>
					</div>
				</Card>
			) : null}

			<div style={{ marginTop: 12 }}>
				{renderRows( draft, {
					elementOptions: options,
					groupTargets,
					lookupTargets: lookupsByName( writableElements( targetElements ) ),
					// {{each.*}} links exist only inside a fan-out naming its card.
					each: fanOut && draft.forEachCardId ? { elements: forEachElements, cardId: draft.forEachCardId, cardName: forEachCardName, kql: draft.forEachVia === 'kql' ? draft.forEachKql : '' } : null,
					scopeName: targetCardName,
					blocked: draft.cardId ? null : 'Pick the card to create first, then choose which of its elements to fill in.',
					// {{each.*}} only exists inside a fan-out, and only types
					// against a named card — so the pills appear exactly when the
					// API would accept them.
					extraTokens: fanOut && draft.forEachCardId ? buildEachTokens( forEachElements ) : null,
				} )}
			</div>
			{linkBackOptions.length ? (
				<div style={{ marginTop: 12 }}>
					<label htmlFor={`linkBack-${ draft.key }`} className='d-block'>Link the new item back</label>
					<Select
						id={`linkBack-${ draft.key }`}
						style={{ width: '100%' }}
						allowClear
						value={draft.linkBackElement || undefined}
						placeholder='Lookup on the new item pointing at the trigger item'
						options={linkBackOptions}
						onChange={value => patchDraft( draft.key, { linkBackElement: value || '' } )}
					/>
				</div>
			) : null}
		</>);
	};

	const elementsChangedOptions = triggerElements
		.filter( el => el?.name )
		.map( el => ( { value: el.name, label: elementLabelOf( el ) } ) );

	return (
		<Modal
			open={open}
			onCancel={onClose}
			onOk={handleSave}
			okText='Save'
			confirmLoading={saving}
			title={rule?._id ? 'Edit rule' : 'New rule'}
			width={760}
			destroyOnClose
		>
			<Space direction='vertical' size={12} style={{ width: '100%' }}>

				<div>
					<label htmlFor='automationRuleName' className='d-block'>Name</label>
					<Input
						id='automationRuleName'
						size='large'
						value={name}
						placeholder='What this rule does, in a few words'
						onChange={e => setName( e.target.value )}
					/>
				</div>

				<InputSelect
					elementName='automationTriggerCard'
					elementLabel='Trigger card'
					value={triggerCardId || undefined}
					onChange={( n, value ) => {
						setTriggerCardId( value || '' );
						setElementsChanged( [] );
						// Another card has other groups: the entry choices start over, and
						// the rule is an item rule again, with the item's default.
						if ( entryGroup ) setEvaluateOn( 'transition' );
						setEntryGroup( '' );
						setColumnsChanged( [] );
						setEntryKql( '' );
					}}
					options={cardOptions}
					placeholder="Which card's items set this rule off?"
					allowClear={false}
				/>

				{triggerCardId && ( triggerGroups.length || watchesEntries ) ? (
					<div>
						<label htmlFor='automationWatch' className='d-block'>What sets it off</label>
						<Select
							id='automationWatch'
							style={{ width: '100%' }}
							value={entryGroup}
							options={[
								{ value: '', label: 'An item' },
								...triggerGroups.map( group => ( { value: group.name, label: `An entry in ${ elementLabelOf( group ) }` } ) ),
							]}
							onChange={( value ) => {
								setEntryGroup( value || '' );
								setColumnsChanged( [] );
								setEntryKql( '' );
								// The item condition's default differs between the two (see ITEM_EVALUATE_ON_FOR_ENTRIES).
								setEvaluateOn( value ? 'match' : 'transition' );
							}}
						/>
						{watchesEntries && ! watchedGroup ? (
							<Text type='danger' className='d-block'>This card no longer has that repeating group.</Text>
						) : null}
					</div>
				) : null}

				{triggerCardId && watchesEntries ? (<>
					<div>
						<Text strong className='d-block'>When an entry is</Text>
						<Checkbox.Group options={ENTRY_EVENT_OPTIONS} value={entryEvents} onChange={setEntryEvents} />
						<Text type='secondary' className='d-block'>
							The rule&apos;s actions run once for each entry that sets it off. Moving an entry, or deleting the whole item, sets nothing off.
						</Text>
					</div>

					{entryEvents.includes( 'changed' ) ? (
						<div>
							<label htmlFor='automationColumnsChanged' className='d-block'>Only when these columns change</label>
							<Select
								id='automationColumnsChanged'
								mode='multiple'
								allowClear
								style={{ width: '100%' }}
								value={columnsChanged}
								placeholder='Any change to the entry (leave empty)'
								options={watchedColumns.map( col => ( { value: col.name, label: elementLabelOf( col ) } ) )}
								onChange={setColumnsChanged}
							/>
							{calculatedWatched.length && ! includeDerived ? (
								<Text type='secondary' className='d-block'>
									A calculated column changes only when Keepr recalculates it: turn on Also run when Keepr recalculates a value, or it never sets the rule off.
								</Text>
							) : null}
						</div>
					) : null}

					<div>
						<Text strong className='d-block'>Only if the entry</Text>
						<ConditionBuilder
							value={entryKql}
							onChange={setEntryKql}
							elements={watchedColumns}
							collectionId={collectionId}
							systemFields={false}
						/>
						<Text type='secondary'>Its own columns. Validated on save.</Text>
					</div>

					{entryKql.trim() && entryEvents.includes( 'changed' ) ? (
						<div>
							<label htmlFor='automationEntryEvaluateOn' className='d-block'>Fire for a changed entry</label>
							<Select
								id='automationEntryEvaluateOn'
								style={{ width: '100%' }}
								value={entryEvaluateOn}
								options={EVALUATE_ON_OPTIONS}
								onChange={setEntryEvaluateOn}
							/>
						</div>
					) : null}
				</>) : null}

				{triggerCardId && ! watchesEntries ? (<>
					<div>
						<Text strong className='d-block'>When an item is</Text>
						<Checkbox.Group options={EVENT_OPTIONS} value={events} onChange={setEvents} />
					</div>
				</>) : null}

				{triggerCardId ? (<>
					{! watchesEntries && listensForTags( events ) ? (
						<div data-testid='automation-trigger-tags'>
							<span className='d-block'>With one of these tags</span>
							<TagSelect
								multiple
								value={triggerTagIds}
								collectionTags={collectionTags}
								privateTags={null}
								searchCollectionId={collectionId}
								placeholder='Which tags?'
								aria-label='With one of these tags'
								onChange={ids => setTriggerTagIds( ids )}
							/>
							<Text type='secondary' className='d-block'>
								Tagged: one of these tags goes on an item that did not have it. Untagged: it comes off. By hand or by a rule; private tags never set a rule off.
							</Text>
						</div>
					) : null}

					{! watchesEntries ? (
						<div>
							<label htmlFor='automationElementsChanged' className='d-block'>Only when these elements change</label>
							<Select
								id='automationElementsChanged'
								mode='multiple'
								allowClear
								style={{ width: '100%' }}
								value={elementsChanged}
								placeholder='Any change (leave empty)'
								options={elementsChangedOptions}
								onChange={setElementsChanged}
							/>
						</div>
					) : null}

					<ToggleRow
						first
						label='Also run when Keepr recalculates a value'
						description={'Calculated elements are written by the platform, not by a person. '
							+ 'Ordinary rules ignore those writes; turn this on for a rule that watches a calculated value.'}
						checked={includeDerived}
						onChange={setIncludeDerived}
					/>

					{alwaysRunApplies( watchesEntries, events ) ? <AlwaysRunToggle checked={alwaysRun} onChange={setAlwaysRun} /> : null}

					<div>
						<Text strong className='d-block'>{watchesEntries ? 'Only if the item' : 'Only if'}</Text>
						<ConditionBuilder
							value={kql}
							onChange={setKql}
							elements={triggerElements}
							collectionId={collectionId}
						/>
						<Text type='secondary'>
							{watchesEntries
								? `The item that holds the entries. To ask about every entry (all done, any done, how many), write it in the text box, e.g. "${ wholeGroupExample }". Validated on save.`
								: 'Validated on save'}
						</Text>
					</div>

					{( kql.trim() || related ) ? (
						<div>
							<label htmlFor='automationEvaluateOn' className='d-block'>{watchesEntries ? 'Check the item' : 'Fire'}</label>
							<Select
								id='automationEvaluateOn'
								style={{ width: '100%' }}
								value={evaluateOn}
								options={watchesEntries ? ITEM_EVALUATE_ON_FOR_ENTRIES : EVALUATE_ON_OPTIONS}
								onChange={setEvaluateOn}
							/>
						</div>
					) : null}

					{related ? (
						<Alert
							type='info'
							showIcon
							message='This rule also has an advanced cross-item condition (edited via the API); it will be kept as-is.'
						/>
					) : null}

					{droppedActions ? (
						<Alert
							type='warning'
							showIcon
							message={`${ droppedActions } action${ droppedActions === 1 ? '' : 's' } on this rule can't be edited here and will be removed if you save.`}
						/>
					) : null}

					<div>
						<Text strong className='d-block'>Then</Text>
						{drafts.map( ( draft, index ) => (
							<Card
								key={draft.key}
								size='small'
								style={{ marginBottom: 8 }}
								title={`${ index + 1 }. ${ ACTION_LABELS[ draft.type ] || draft.type }`}
								extra={(
									<Button
										type='text'
										danger
										icon={<CLOSE />}
										aria-label={`Remove action ${ index + 1 }`}
										onClick={() => setDrafts( prev => prev.filter( d => d.key !== draft.key ) )}
									/>
								)}
							>
								{renderDraft( draft )}
							</Card>
						) )}
						<Dropdown trigger={[ 'click' ]} menu={addActionMenu}>
							<Button icon={<PLUS />}>Add action</Button>
						</Dropdown>
					</div>
				</>) : null}


				<QueryRefusalPick
					inline
					refusal={elementPicks.refusal}
					disabled={saving}
					onPick={value => { elementPicks.pick( value ); handleSave(); }}
					onRepick={() => { elementPicks.clearRefusal(); handleSave(); }}
				/>
			</Space>
		</Modal>
	);
}
